import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import FormLabel from '@mui/material/FormLabel';
import { DEFAULT_PRIMARY_FONT_FAMILY } from 'theme/fontDefaults';

export const StInputBase = styled(InputBase)(({ theme }) => ({
  background: theme.palette.white.main,
  borderRadius: '4px',
  padding: '0 2px 0 8px',
  marginTop: '2px',
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 400,
  height: '40px',
  '&:hover': {
    border: `1px solid ${theme.palette.neutral6.main}`,
  },
  '&.Mui-focused': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& ::placeholder': {
    color: `${theme.palette.neutral8.main} !important`,
    fontFamily: `${
      theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
    }, sans-serif`,
    fontSize: '13px',
    lineHeight: '16px',
  },
}));

export const StFormLabel = styled(FormLabel)(() => ({
  lineHeight: '21px',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
}));

export const StFormDiv = styled('div')(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  color: `${theme.palette.neutral8.main}`,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  '&.Mui-focused': {
    color: `${theme.palette.neutral8.main}`,
  },
}));

export const StCheckBoxLabel = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  paddingBottom: '6px',
  color: `${theme.palette.neutral8.main}`,
}));

export const StPlaceholderTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral10.main,
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '13px',
  lineHeight: '16px',
  padding: '4px 0 5px',
}));

/** Outlined contact-info email `Autocomplete` — scoped helper, does not change `Input` / `StInputBase`. */
export const contactInfoEmailAutocompleteSx = (
  theme: Theme,
  hasError: boolean
): SxProps<Theme> => ({
  mt: '2px',
  background: theme.palette.white.main,
  borderRadius: '4px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    boxSizing: 'border-box',
    padding: '5px 8px !important',
    alignItems: 'center',
    fontFamily: `${
      theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
    }, sans-serif`,
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
  },
  '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
    padding: '0 !important',
    lineHeight: '24px',
  },
  '& .MuiOutlinedInput-input::placeholder, & .MuiAutocomplete-input::placeholder': {
    color: `${theme.palette.neutral8.main} !important`,
    fontFamily: `${
      theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
    }, sans-serif`,
    fontSize: '13px',
    lineHeight: '16px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${
      hasError ? theme.palette.error.main : theme.palette.neutral4.main
    } !important`,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${hasError ? theme.palette.error.main : theme.palette.neutral6.main} !important`,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${hasError ? theme.palette.error.main : theme.palette.primary.main} !important`,
    borderWidth: '1px',
  },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
  '& .MuiAutocomplete-popupIndicator': { padding: '4px' },
});
